import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useParams } from "react-router-dom";
import {
	Badge,
	Box,
	Card,
	Center,
	Container,
	Flex,
	Group,
	Image,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { PrintDate } from "@/common_function/function";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";

import { ContactUsForm } from "@/components/ContactUsForm/ContactUsForm";
import { ImageContainer, ThemedTitle, TitleContainer } from "@/components/HeaderContainer";
import { ThemedButton, ThemedCard } from "@/components/ThemedButton";

import generalApi from "@/services/general-api";

import defaultCourse from "@/assets/img/default_course.jpg";

import { ReactComponent as BlueChecked } from "./assets/blue-checked.svg";
import { ReactComponent as GreenTrophy } from "./assets/green-trophy.svg";

function ModalBundleInfo() {
	const params = useParams();
	const [selectedCategory, setSelectedCategory] = useState(null);

	const handleCategoryClick = (category) => {
		setSelectedCategory(category);
	};

	const query = useQuery({
		queryKey: ["bundle", params.id],
		queryFn: () => generalApi.getBundleDetail(params.id),
	});

	const bundle = query.data;

	if (query.isLoading) return <LoadingPlaceholder />;
	if (query.isError) return <Text>Error</Text>;

	if (!bundle) return null;

	const courseList = bundle.courses.sort((a, b) => {
		if (a.has_registered && !b.has_registered) return -1;
		if (!a.has_registered && b.has_registered) return 1;
		if (a.is_completed && !b.is_completed) return -1;
		if (!a.is_completed && b.is_completed) return 1;
		if (a.is_completed === b.is_completed) {
			return b.registered_at - a.registered_at;
		}
		return b.is_completed - a.is_completed;
	});

	const category_list = bundle.category_list || [];

	return (
		<Box>
			<TitleContainer order={2}>Professional Certificate Attainment</TitleContainer>
			<motion.div
				initial={{ opacity: 0.2 }}
				whileInView={{ opacity: 1 }}
				viewport={{ once: true }}
				transition={{ duration: 1, ease: "easeOut" }}>
				<ImageContainer imageUrl={bundle?.bundle_banner_image}>
					<Flex
						direction="column"
						justify={"center"}
						mih={300}
						maw={{
							base: "80%",
							sm: "40%",
						}}>
						<motion.div
							initial={{ opacity: 0, y: 50 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{
								duration: 0.5,
								ease: "easeOut",
							}}>
							<Title c="themed.8" order={2} fw={800} lh={1}>
								{bundle.bundle_banner_title}
							</Title>
							<Text c="#003640" size="md" mt={10}>
								{bundle.bundle_banner_description}
							</Text>
						</motion.div>
					</Flex>
				</ImageContainer>
			</motion.div>

			<TitleContainer>
				<ThemedTitle order={3}>Course Progression</ThemedTitle>
			</TitleContainer>
			<Card>
				<Container>
					<Stack my={20}>
						<ThemedTitle order={3}>{bundle.bundle_name}</ThemedTitle>
						<Group bg="green.1" c="green.9" p={10} gap={10} w="fit-content" justify="start">
							<i className="fa fa-info-circle" />
							<Text>
								{bundle.total_courses || 0} Courses Available, {bundle.total_registered || 0}{" "}
								Registered, {bundle.total_completed || 0} Completed
							</Text>
						</Group>
					</Stack>

					<Group gap={10} bdrs={10} bd={"1px solid #E7EBEC"} p={10} px={20} w="fit-content">
						<CategoryBadge
							variant={selectedCategory === null ? "filled" : "transparent"}
							c={selectedCategory === null ? undefined : "black"}
							onClick={() => handleCategoryClick(null)}>
							All
						</CategoryBadge>
						{category_list.map((category) => {
							return (
								<CategoryBadge
									key={category.category_title}
									// color={category.category_color}
									variant={
										selectedCategory?.category_title === category.category_title
											? "filled"
											: "transparent"
									}
									color={
										selectedCategory?.category_title === category.category_title
											? "themed.8"
											: "black"
									}
									onClick={() => handleCategoryClick(category)}>
									{category.category_title}
								</CategoryBadge>
							);
						})}
					</Group>
					<Box mt={20}>
						{selectedCategory ? (
							<SimpleGrid cols={{ base: 1, xs: 2 }}>
								{selectedCategory.courses.map((course, i) => {
									return (
										<motion.div
											key={course.course_id + selectedCategory.category_title}
											initial={{ opacity: 0, y: 50 }}
											whileInView={{ opacity: 1, y: 0 }}
											viewport={{ once: true }}
											transition={{ duration: 0.5, ease: "easeOut", delay: (i % 2) * 0.1 }}>
											<CourseCard key={i} course={course} />
										</motion.div>
									);
								})}
							</SimpleGrid>
						) : (
							<SimpleGrid cols={{ base: 1, xs: 2 }}>
								{courseList.map((course, i) => {
									return (
										<motion.div
											key={course.course_id}
											initial={{ opacity: 0, y: 50 }}
											whileInView={{ opacity: 1, y: 0 }}
											viewport={{ once: true }}
											transition={{ duration: 0.5, ease: "easeOut", delay: (i % 2) * 0.1 }}>
											<CourseCard key={i} course={course} />
										</motion.div>
									);
								})}
							</SimpleGrid>
						)}
					</Box>
				</Container>
			</Card>
		</Box>
	);
}

const LoadingPlaceholder = () => {
	return (
		<Box bg="whtie">
			<TitleContainer>
				<Skeleton height={30} width={"50%"} />
			</TitleContainer>
			<Skeleton height={300} width={"100%"} />
			<TitleContainer>
				<Center>
					<Skeleton height={30} width={"50%"} />
				</Center>
			</TitleContainer>
			<Card w="100%">
				<Container size={"xl"} w="100%">
					<SimpleGrid cols={{ base: 1, xs: 2 }} w="100%">
						{Array.from({ length: 8 }).map((_, i) => (
							<Skeleton key={i} height={200} width={"100%"} />
						))}
					</SimpleGrid>
				</Container>
			</Card>
		</Box>
	);
};

const CourseCard = ({ course }) => {
	const handleEnquire = (course) => {
		// console.log(course);
		const modalId = modals.open({
			size: "xl",
			children: (
				<ContactUsForm
					onSuccess={() => {
						modals.close(modalId);
					}}
					data={{
						remarks: `I want to enquire about the ${course.course_title} course`,
					}}
				/>
			),
		});
	};

	let courseColor;
	if (course.has_registered && course.is_completed) {
		courseColor = { color: "#00ab84" };
	} else if (course.has_registered && !course.is_completed) {
		courseColor = { color: "#101820" };
	} else {
		courseColor = { color: "#aaaaaa" };
	}

	// TODO: open our cert
	return (
		<Link to={course.view_cert_url || course.fca_seo_url || null}>
			<ThemedCard
				bg={course.has_registered || course.is_completed ? "#F0FDFF" : "#F6F6F6"}
				shadow="1px 2px 2px 1px rgba(0, 0, 0, 0.2)"
				h={"100%"}
				p={5}>
				<Flex direction={{ base: "column-reverse", md: "row" }} h="100%">
					<Flex
						direction="column"
						flex="1 1 auto"
						p="sm"
						justify="space-between"
						mih={{ base: 150 }}>
						<Box>
							<Text size="sm" fw={700} lh={1.2} style={courseColor}>
								{course.course_title}
							</Text>
							<Text size="sm" lh={1.2} c="black" mt={5}>
								{course.course_ref}
							</Text>

							{course.has_registered && (
								<>
									{course.courserun_course_from ? (
										<p className="text-fca-dark fs14 mt-2">
											Course Date: {course.courserun_course_from} to {course.courserun_course_to}
										</p>
									) : null}
								</>
							)}
							{course.trainer_name && (
								<p className="text-fca-dark fs14 mt-2">Trainer: {course.trainer_name}</p>
							)}
						</Box>
						<Stack gap={5} mt={10}>
							{course.has_registered && course.is_completed && (
								<CourseStatusBadge
									leftSection={<GreenTrophy width={11} height={11} />}
									color="#00AB84">
									Completed
								</CourseStatusBadge>
							)}

							{course.has_registered && !course.is_completed && (
								<CourseStatusBadge
									leftSection={<BlueChecked width={11} height={11} />}
									color="#34AA80">
									Registered
								</CourseStatusBadge>
							)}

							{!course.has_registered && (
								<>
									<Group>
										<ThemedButton
											px={20}
											size="compact-sm"
											rightSection={<i className="fa fa-angle-right" />}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												handleEnquire(course);
											}}>
											Enquire
										</ThemedButton>
									</Group>
								</>
							)}
						</Stack>
					</Flex>
					<Image
						h={"auto"}
						w={{ base: "100%", md: "40%" }}
						bg="white"
						bdrs={20}
						style={{ aspectRatio: 16 / 9 }}
						fit={"cover"}
						src={course.course_banner_image || defaultCourse}
					/>
				</Flex>
			</ThemedCard>
		</Link>
	);
};

const CategoryBadge = Badge.withProps({
	bdrs: "sm",
	tt: "none",
	px: 20,
	size: "xl",
	fw: 500,
	color: "themed.8",
	style: { cursor: "pointer" },
});

const CourseStatusBadge = Badge.withProps({
	variant: "outline",
	bdrs: 20,
	tt: "none",
	px: 20,
	size: "lg",
	fw: 500,
});

export default ModalBundleInfo;
